// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { ageOnDate, DOB_MAX_AGE_YEARS, DOB_MIN_AGE_YEARS } from "@bitebalance/contracts"
import { Field } from "../Field"
import { Input } from "@/components/ui/input"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft | ((prev: AnswerDraft) => AnswerDraft)) => void
  onIneligible: () => void
}

/**
 * Step 2 (wire) — 18+/Germany eligibility gate. The contract
 * (`Step2Schema`) only has a shape for the *eligible* answer
 * (`ageEligible18Plus: literal(true)`); there is no server representation
 * for "ineligible", so choosing that option never writes a draft answer —
 * it only raises the ineligibility blocker (ADR-013 Decision 3), which
 * keeps the flow off the eligible-only wire contract entirely.
 */
export function Step2Eligibility({ value, onChange, onIneligible }: Props) {
  const { t } = useI18n()
  const eligible = value.ageEligible18Plus === true
  const firstName = (value.firstName as string | undefined) ?? ""
  const familyName = (value.familyName as string | undefined) ?? ""
  const dateOfBirth = (value.dateOfBirth as string | undefined) ?? ""
  // 2026-08 profile batch: DOB is asked exactly where the 18+ confirmation
  // lives. An underage date contradicts the claim above — surfaced inline
  // (Continue stays disabled via the steps.ts gate; the server re-checks
  // authoritatively with DOB_UNDERAGE).
  const dobComplete = /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)
  const dobAge = dobComplete ? ageOnDate(dateOfBirth) : null
  const dobUnderage = dobAge !== null && dobAge < DOB_MIN_AGE_YEARS
  const dobImplausible = dobAge !== null && dobAge > DOB_MAX_AGE_YEARS

  // Names are OPTIONAL (ADR-034): an empty string must be OMITTED from the
  // answer, not sent — Step2Schema's min(1) would reject "" and silently
  // pin Continue disabled.
  const patchEligible = (patch: Record<string, unknown>) => {
    const merged: Record<string, unknown> = {
      ageEligible18Plus: true,
      country: "DE",
      firstName,
      familyName,
      dateOfBirth,
      ...patch,
    }
    for (const key of ["firstName", "familyName", "dateOfBirth"]) {
      if (typeof merged[key] === "string" && (merged[key] as string).trim() === "") {
        delete merged[key]
      }
    }
    onChange(() => merged)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-2" role="radiogroup" aria-label={t("onboarding.step2.title")}>
        <button
          type="button"
          role="radio"
          aria-checked={eligible}
          onClick={() => patchEligible({})}
          className={`min-h-11 rounded-bb-md border px-4 py-3 text-left ${eligible ? "border-green bg-green/10" : "border-line bg-paper hover:border-green/60"}`}
        >
          <span className="block font-semibold text-ink">
            {t("onboarding.step2.eligibleOption")}
          </span>
          <span className="block text-xs text-ink-2">{t("onboarding.step2.eligibleSub")}</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={false}
          onClick={onIneligible}
          className="min-h-11 rounded-bb-md border border-line bg-paper px-4 py-3 text-left hover:border-destructive/60"
        >
          <span className="block font-semibold text-ink">
            {t("onboarding.step2.ineligibleOption")}
          </span>
          <span className="block text-xs text-ink-2">{t("onboarding.step2.ineligibleSub")}</span>
        </button>
      </div>

      {eligible && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("onboarding.step2.firstNameLabel")}
              htmlFor="onboarding-first-name"
              required={false}
            >
              <Input
                id="onboarding-first-name"
                autoComplete="given-name"
                value={firstName}
                onChange={e => patchEligible({ firstName: e.target.value })}
              />
            </Field>
            <Field
              label={t("onboarding.step2.familyNameLabel")}
              htmlFor="onboarding-family-name"
              required={false}
            >
              <Input
                id="onboarding-family-name"
                autoComplete="family-name"
                value={familyName}
                onChange={e => patchEligible({ familyName: e.target.value })}
              />
            </Field>
          </div>
          <p className="text-xs text-ink-2">{t("onboarding.step2.namesOptionalHint")}</p>

          <Field label={t("onboarding.step2.dobLabel")} htmlFor="onboarding-dob">
            <Input
              id="onboarding-dob"
              type="date"
              autoComplete="bday"
              value={dateOfBirth}
              onChange={e => patchEligible({ dateOfBirth: e.target.value })}
            />
            <p className="text-xs text-ink-2">{t("onboarding.step2.dobHint")}</p>
            {dobUnderage && (
              <p className="text-xs text-destructive">{t("onboarding.step2.dobUnderage")}</p>
            )}
            {dobImplausible && (
              <p className="text-xs text-destructive">{t("onboarding.step2.dobInvalid")}</p>
            )}
          </Field>
        </>
      )}
    </div>
  )
}
